import YearlyWozReport from '@components/WozResearch/YearlyWozReport'
import ArticleHero from '@components/marketing-website/ArticleHero'
import InlineWozCheckBox from '@components/marketing-website/InlineWozCheckBox'
import PlainContent from '@components/marketing-website/PlainContent'
import { PlainContentWrapper } from '@components/marketing-website/PlainContent/styles'
import Accordion from '@components/mewoz/Accordion'
import Seo from '@components/shared/Seo'
import { Column, Row, SavingsOrValueWrapper } from '@components/woz-check/WozCheckResult/styles'
import LocationLayout from '@layouts/LocationLayout'
import { SideBar } from '@layouts/LocationLayout/props'
import { BreadcrumbWrapper, Footnote, Savings } from '@layouts/LocationLayout/styles'
import { parseCell, parseCellHTML } from '@utils/parseCell'
import withStateMachine from '@utils/withStateMachine'
import { PageProps, graphql } from 'gatsby'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { ACTIVE_YEAR } from '../constants'
import { PageContextProps } from '../types'

const CityPageV2Template: React.FC<PageProps<Queries.cityV2Query, PageContextProps>> = ({ data, location }) => {
  const { seoCitiesCsv: page } = data
  const { strapiPage: ywozContent } = data

  const active_year = page?.tax_year

  const tax_damage = parseInt(page?.tax_damage.replace('€ ', '') || 0)
  const high_woz_avg = parseInt(page?.high_woz_avg * 1000 || 0)
  const high_ewoz_avg = parseInt(page?.high_ewoz_avg * 1000 || 0)
  const ozb_tarief = parseFloat((page?.ozb_tarief || '').replace(',', '.') || 0) / 100
  const water_tarief = parseFloat((page?.water_tarief || '').replace(',', '.') || 0)

  const ozb_savings = Math.round(high_woz_avg * ozb_tarief - high_ewoz_avg * ozb_tarief)
  const water_savings = Math.round(high_woz_avg * water_tarief - high_ewoz_avg * water_tarief)
  const income_savings = tax_damage - ozb_savings - water_savings

  const sidebar = {
    items: [
      {
        title: page?.section1_heading,
        anchor_element_id: page?.section1_heading?.replaceAll(' ', '-'),
      },
      {
        title: page?.section2_heading,
        anchor_element_id: page?.section2_heading?.replaceAll(' ', '-'),
      },
      {
        title: page?.section3_heading,
        anchor_element_id: page?.section3_heading?.replaceAll(' ', '-'),
      },
      {
        title: page?.section3_heading_b,
        anchor_element_id: page?.section3_heading_b?.replaceAll(' ', '-'),
      },
      {
        title: page?.section4_heading,
        anchor_element_id: page?.section4_heading?.replaceAll(' ', '-'),
      },
      {
        title: page?.section5_heading,
        anchor_element_id: page?.section5_heading?.replaceAll(' ', '-'),
      },
      {
        title: page?.section6_heading,
        anchor_element_id: page?.section6_heading?.replaceAll(' ', '-'),
      },
    ],
  }

  return (
    <LocationLayout
      sidebar={sidebar as SideBar}
      staticArticleHero={
        <ArticleHero
          image={<img src={'/images/windmolen.jpeg'} alt={page?.meta_title || ''} />}
          title={page?.hero_heading || ''}
        />
      }
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Gemeenten',
                item: `${process.env.GATSBY_SITE_URL}/woz-waarde-per-gemeente`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: page?.municipality,
                item: `${process.env.GATSBY_SITE_URL}/${page?.parent_url}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: page?.city,
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: page?.section1_heading,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: parseCell(page?.section1_paragraph, page),
                },
              },
              {
                '@type': 'Question',
                name: page?.section2_heading,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: parseCell(page?.section2_paragraph, page),
                },
              },
              {
                '@type': 'Question',
                name: page?.section3_heading,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: parseCell(page?.section3_paragraph, page),
                },
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'HowTo',
            name: page?.section5_heading,
            description: page?.section5_paragraph,
            image: 'https://eerlijkewoz.nl/images/windmolen.jpeg',
            step: [
              {
                '@type': 'HowToStep',
                text: page?.howto1_heading,
              },
              {
                '@type': 'HowToStep',
                text: page?.howto2_heading,
              },
              {
                '@type': 'HowToStep',
                text: page?.howto3_heading,
              },
              {
                '@type': 'HowToStep',
                text: page?.howto4_heading,
              },
              {
                '@type': 'HowToStep',
                text: page?.howto5_heading,
              },
              {
                '@type': 'HowToStep',
                text: page?.howto6_heading,
              },
              {
                '@type': 'HowToStep',
                text: page?.howto7_heading,
              },
            ],
          })}
        </script>
      </Helmet>
      <Seo title={page?.meta_title || ''} description={page?.og_description || ''} index={true} location={location} />
      <BreadcrumbWrapper>
        <li>
          <a href={`${process.env.GATSBY_SITE_URL}/woz-waarde-per-gemeente`}>Gemeenten</a>
          <span>›</span>
        </li>
        <li>
          <a href={`${process.env.GATSBY_SITE_URL}/${page?.parent_url}`}>{page?.municipality}</a>
          <span>›</span>
        </li>
        <li>{page?.city}</li>
      </BreadcrumbWrapper>
      <div id={page?.section1_heading?.replaceAll(' ', '-')} />
      <PlainContent
        noMargin
        content={`
              <h2>${page?.section1_heading}</h2>
              <p>${parseCellHTML(page?.section1_paragraph)}</p>
              <h4>Overzicht WOZ-waarden ${page?.city}*</h4>
              <table class='municipality-table'>
                <tbody>
                  <tr>
                    <td><strong>WOZ-waarde ${active_year - 1}</strong></td>
                    <td>${page?.avg_woz_min_1}</td>
                  </tr>
                  <tr>
                    <td><strong>WOZ-waarde ${active_year}</strong></td>
                    <td>${page?.avg_woz}</td>
                  </tr>
                  <tr>
                    <td><strong>${parseFloat(page?.woz_increase) < 0 ? 'Verschil' : 'Stijging'} WOZ-waarde</strong></td>
                    <td>${page?.woz_increase}%</td>
                  </tr>
                  <tr>
                    <td><strong>WOZ-waarden te hoog</strong></td>
                    <td>${page?.perc_to_high}</td>
                  </tr>
                  <tr>
                    <td><strong>Teveel betaalde belasting</strong></td>
                    <td>${page?.tax_damage}</td>
                  </tr>
                </tbody>
              </table>
                   `}
      />
      <Footnote>
        * De cijfers op deze pagina zijn een inschatting op basis van de meest recente informatie die bij ons bekend is.
      </Footnote>
      <InlineWozCheckBox heading={`Doe nu de gratis WOZ Check ${ACTIVE_YEAR}`} />
      <div id={page?.section2_heading?.replaceAll(' ', '-')} />
      <PlainContentWrapper>
        <h3>{page?.section2_heading}</h3>
        <p>{parseCell(page?.section2_paragraph, page)}</p>
      </PlainContentWrapper>

      <div id={page?.section3_heading?.replaceAll(' ', '-')} />
      <PlainContent
        content={`
            <h3>${page?.section3_heading}</h3>
            <p>${parseCell(page?.section3_paragraph, page)}</p>`}
      />

      <div id={page?.section3_heading_b?.replaceAll(' ', '-')} />
      <PlainContent
        content={`
            <h3>${page?.section3_heading_b}</h3>
            <p>
              De WOZ-waarde wordt gebruikt om allerlei soorten belastingen van de rijksoverheid en de gemeente te berekenen. Wilt u meer weten hoe u uw WOZ-waarde kunt verlagen en op welke belastingen de WOZ-waarde invloed heeft? Dat kunt u eenvoudig terugvinden 
              <a href="/woz-verlagen">in dit artikel</a>.
            </p>
            <p>Hier beneden treft u de gemiddelde besparingen per jaar op basis van uw woonplaats. Let op, de onderstaande cijfers zijn gebaseerd op de gemiddelden in ${page?.city}. Het bedrag kan dus hoger of lager uitvallen.</p>
            <h4>Verwachte gemiddelde besparingen per jaar op basis van de WOZ-waarde in ${page?.city}</h4>`}
      />

      <Savings>
        <SavingsOrValueWrapper className={'savings'}>
          <Row>
            <Column>Inkomstenbelasting</Column>
            <Column>€ {income_savings}</Column>
          </Row>
          <Row>
            <Column>Onroerende-zaakbelasting (OZB)</Column>
            <Column>€ {ozb_savings}</Column>
          </Row>
          <Row>
            <Column>Watersysteemheffing</Column>
            <Column>€ {water_savings}</Column>
          </Row>
        </SavingsOrValueWrapper>
        <Row className={'total'}>
          <Column isTotalField>Totale besparing</Column>
          <Column isTotalField>{page?.tax_damage}</Column>
        </Row>
      </Savings>

      <div id={page?.section4_heading?.replaceAll(' ', '-')} />
      <PlainContent
        content={`
            <h2>${page?.section4_heading}</h2>
            <p>${parseCell(page?.section4_paragraph, page)}</p>
            <p>${page?.section4_paragraph_2}</p>
            `}
      />
      <div id={page?.section5_heading?.replaceAll(' ', '-')} />
      <PlainContent
        content={`
            <h3>${page?.section5_heading}</h3>
            <p>${parseCell(page?.section5_paragraph, page)}</p>
            `}
      />
      <PlainContentWrapper>
        <Accordion
          defaultOpen={null}
          items={[
            {
              title: page?.howto1_heading || '',
              content: `<p>${parseCellHTML(page?.howto1_paragraph, page)}</p>`,
            },
            {
              title: page?.howto2_heading || '',
              content: `<p>${parseCellHTML(page?.howto2_paragraph, page)}</p>`,
            },
            {
              title: page?.howto3_heading || '',
              content: `<p>${parseCellHTML(page?.howto3_paragraph, page)}</p>`,
            },
            {
              title: page?.howto4_heading || '',
              content: `<p>${parseCellHTML(page?.howto4_paragraph, page)}</p>`,
            },
            {
              title: page?.howto5_heading || '',
              content: `<p>${parseCellHTML(page?.howto5_paragraph, page)}</p>`,
            },
            {
              title: page?.howto6_heading || '',
              content: `<p>${parseCellHTML(page?.howto6_paragraph, page)}</p>`,
            },
            {
              title: page?.howto7_heading || '',
              content: `<p>
              ${parseCell(page?.howto7_paragraph, page)}
              <ul>
                <li>Kloppen de gebruikte kenmerken van de woning?</li>
                <li>Zijn de verkochte referentiewoningen vergelijkbaar?</li>
                <li>Heeft de gemeente voldoende gereageerd op de argumenten van degene die het beroep instelt?</li>
              </ul>
            </p>`,
            },
          ]}
        />
      </PlainContentWrapper>
      <div id={page?.section6_heading?.replaceAll(' ', '-')} />
      <PlainContent
        content={`
            <h3>${page?.section6_heading}</h2>
            <p>${parseCell(page?.section6_paragraph, page)}</p>
            `}
      />
      {ywozContent?.sections && (
        <YearlyWozReport
          contentEstimationSavingsTool={ywozContent?.sections?.contentEstimationSavingsTool || ''}
          contentAddressInput={ywozContent?.sections?.contentAddressInput || ''}
          contentIntro={ywozContent?.sections?.contentIntro || ''}
          contentWozIncrease={ywozContent?.sections?.contentWozIncrease || ''}
          contentWozTaxes={ywozContent?.sections?.contentWozTaxes || ''}
        />
      )}
    </LocationLayout>
  )
}

export default withStateMachine(CityPageV2Template)

export const cityV2Query = graphql`
  query cityV2($id: String!) {
    seoCitiesCsv(id: { eq: $id }) {
      url
      parent_url
      hero_heading
      meta_description
      meta_title
      city
      municipality
      og_description
      og_title
      section1_heading
      section1_paragraph
      section2_heading
      section3_heading
      section2_paragraph
      section3_paragraph
      section3_heading_b
      section3_paragraph_b
      section4_heading
      section4_paragraph
      section4_paragraph_2
      section5_heading
      section5_paragraph
      section6_heading
      section6_paragraph
      howto1_heading
      howto1_paragraph
      howto2_heading
      howto2_paragraph
      howto3_heading
      howto3_paragraph
      howto4_heading
      howto4_paragraph
      howto5_heading
      howto5_paragraph
      howto6_heading
      howto6_paragraph
      howto7_heading
      howto7_paragraph
      ozb_tarief
      perc_to_high
      tax_damage
      high_woz_avg
      high_ewoz_avg
      avg_woz_min_1
      water_tarief
      avg_woz
      woz_increase
      population
      sample
      tax_year
    }
    strapiPage(strapiId: { eq: 28 }) {
      sections {
        ...YearlyWozResearch
      }
    }
  }
`
